/**
 * ErrorBoundary - React Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs the error, and displays a fallback UI.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */

import React from 'react';
import { isChunkLoadError, reloadForStaleChunk } from '../utils/lazyWithRetry';
import { BrandLogo } from '@/components/brand/BrandLogo';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  isChunkError: boolean;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    // Stale chunk after a deploy (lazyWithRetry already retried in-place) →
    // reload once. While reloading we render a neutral "Updating…" state (see
    // render) rather than the red error card, so there's no scary flash.
    if (isChunkLoadError(error)) {
      reloadForStaleChunk();
      return;
    }

    // Log to console — ErrorBoundary is a sanctioned console exception (CLAUDE.md).
    // eslint-disable-next-line no-console -- ErrorBoundary diagnostics (sanctioned)
    console.error('ErrorBoundary caught an error:', error);
    // eslint-disable-next-line no-console -- ErrorBoundary diagnostics (sanctioned)
    console.error('Component stack:', errorInfo.componentStack);

    // Sentry will auto-capture if configured
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    // Hard navigation to the app root — a guaranteed escape from a broken route.
    // "Try Again" only clears the error state and re-renders the SAME route, so a
    // persistent error loops; this fetches fresh HTML/chunks and lands on a
    // working page (the dashboard, or login if signed out).
    window.location.assign('/');
  };

  render() {
    if (this.state.hasError) {
      // Stale-chunk error: we're reloading to the latest version — show a neutral
      // "Updating…" state instead of the alarming error card (no scary flash).
      if (this.state.isChunkError) {
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <BrandLogo size="md" />
              </div>
              <div className="w-10 h-10 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm text-gray-500">Updating to the latest version…</p>
            </div>
          </div>
        );
      }

      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
          <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl shadow-lg p-8 text-center">
            <div className="flex justify-center mb-6">
              <BrandLogo size="md" />
            </div>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                role="img"
                aria-label="Error warning icon"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            <div role="alert">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Something went wrong
              </h2>

              <p className="text-gray-600 mb-6 text-sm">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
            </div>

            {import.meta.env.DEV && this.state.errorInfo && (
              <details className="mb-6 text-left">
                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                  View error details
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 rounded text-xs text-gray-700 overflow-auto max-h-40">
                  {this.state.error?.stack}
                </pre>
              </details>
            )}

            <div className="flex flex-col gap-2.5">
              <button
                onClick={this.handleGoHome}
                className="px-5 py-2.5 bg-gray-900 hover:bg-gray-900 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Back to Dashboard
              </button>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={this.handleReload}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Reload Page
                </button>
                <button
                  onClick={this.handleReset}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>

            <p className="mt-6 text-xs text-gray-400">
              If this problem persists, please contact{' '}
              <a href="mailto:support@buzzbitx.com" className="text-gray-900 hover:underline">
                support@buzzbitx.com
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
